<template>
    <div class="row">
        <div class="col-md-6 tw-mb-3">
            <label class="form-col-label control-label">{{
                $t("cashRequest.requestedBy")
            }}</label>
            <div class="form-control bg-light tw-flex tw-items-center tw-space-x-2">
                <i class="fas fa-user-tie text-primary"></i>
                <span>{{ requestedByName }}</span>
            </div>
        </div>

        <div class="col-md-6 tw-mb-3">
            <label class="required form-col-label control-label">{{
                $t("cashRequest.requestDate")
            }}</label>
            <DatePicker
                v-model="model.request_date"
                type="date"
                format="dd-MM-yyyy"
                :placeholder="$t('cashRequest.requestDate')"
                style="width: 100%"
                :class="{ 'is-invalid': validate.has('request_date') }"
            />
            <div class="invalid-feedback" v-if="validate.has('request_date')">
                {{ validate.first("request_date") }}
            </div>
        </div>

        <div class="col-md-6 tw-mb-3">
            <label class="required form-col-label control-label">{{
                $t("cashRequest.amount")
            }}</label>
            <Input
                v-model="model.request_amount"
                type="number"
                :min="0"
                :placeholder="$t('cashRequest.amount')"
                :class="{ 'is-invalid': validate.has('request_amount') }"
            />
            <div class="invalid-feedback" v-if="validate.has('request_amount')">
                {{ validate.first("request_amount") }}
            </div>
        </div>

        <div class="col-md-6 tw-mb-3">
            <label class="required form-col-label control-label">{{
                $t("cashRequest.currency")
            }}</label>
            <Select
                v-model="model.currency_id"
                :placeholder="$t('cashRequest.currency')"
                :class="{ 'is-invalid': validate.has('currency_id') }"
            >
                <Option
                    v-for="cur in currencies"
                    :key="cur.currency_id"
                    :value="cur.currency_id"
                >
                    {{ cur.currency_code }} — {{ cur.currency_name_en }}
                </Option>
            </Select>
            <div class="invalid-feedback" v-if="validate.has('currency_id')">
                {{ validate.first("currency_id") }}
            </div>
        </div>

        <div class="col-md-6 tw-mb-3">
            <label class="required form-col-label control-label">{{
                $t("cashRequest.bank")
            }}</label>
            <Select
                v-model="model.bank_id"
                :placeholder="$t('cashRequest.bank')"
                filterable
                :class="{ 'is-invalid': validate.has('bank_id') }"
                @on-change="onBankChange"
            >
                <Option
                    v-for="bank in banks"
                    :key="bank.bank_id"
                    :value="bank.bank_id"
                    :label="bank.bank_name"
                >
                    {{ bank.bank_name }}
                </Option>
            </Select>
            <div class="invalid-feedback" v-if="validate.has('bank_id')">
                {{ validate.first("bank_id") }}
            </div>
        </div>

        <div class="col-md-6 tw-mb-3">
            <label class="required form-col-label control-label">{{
                $t("cashRequest.bankAccount")
            }}</label>
            <Select
                v-model="selectedAccountId"
                :placeholder="$t('cashRequest.bankAccount')"
                filterable
                :disabled="!model.bank_id"
                :class="{ 'is-invalid': validate.has('bank_account_no') }"
                @on-change="onAccountChange"
            >
                <Option
                    v-for="acc in filteredAccounts"
                    :key="acc.account_id"
                    :value="acc.account_id"
                    :label="acc.account_no"
                >
                    {{ acc.account_no }} — {{ acc.account_name }}
                </Option>
            </Select>
            <div class="invalid-feedback" v-if="validate.has('bank_account_no')">
                {{ validate.first("bank_account_no") }}
            </div>
        </div>

        <div class="col-md-6 tw-mb-3">
            <label class="required form-col-label control-label">{{
                $t("cashRequest.bankAccountNo")
            }}</label>
            <Input
                v-model="model.bank_account_no"
                :placeholder="$t('cashRequest.bankAccountNo')"
                :class="{ 'is-invalid': validate.has('bank_account_no') }"
            />
            <div class="invalid-feedback" v-if="validate.has('bank_account_no')">
                {{ validate.first("bank_account_no") }}
            </div>
        </div>

        <div class="col-md-6 tw-mb-3">
            <label class="required form-col-label control-label">{{
                $t("cashRequest.bankAccountName")
            }}</label>
            <Input
                v-model="model.bank_account_name"
                :placeholder="$t('cashRequest.bankAccountName')"
                :class="{ 'is-invalid': validate.has('bank_account_name') }"
            />
            <div class="invalid-feedback" v-if="validate.has('bank_account_name')">
                {{ validate.first("bank_account_name") }}
            </div>
        </div>

        <div class="col-md-12 tw-mb-3">
            <label class="form-col-label control-label">{{
                $t("cashRequest.purpose")
            }}</label>
            <textarea
                v-model.trim="model.purpose"
                rows="3"
                class="form-control"
                :placeholder="$t('cashRequest.purpose')"
            ></textarea>
        </div>

        <div class="col-md-12 tw-mb-3">
            <label class="form-col-label control-label">{{
                $t("cashRequest.remark")
            }}</label>
            <textarea
                v-model.trim="model.remark"
                rows="2"
                class="form-control"
                :placeholder="$t('cashRequest.remark')"
            ></textarea>
        </div>
    </div>
</template>

<script>
export default {
    name: "CashRequestFormInput",
    props: {
        value: { type: Object, required: true },
        validate: { type: Object, required: true },
        requestedByName: { type: String, default: "" },
        currencies: { type: Array, default: () => [] },
        banks: { type: Array, default: () => [] }
    },
    data() {
        return {
            selectedAccountId: null
        };
    },
    computed: {
        model() {
            return this.value;
        },
        filteredAccounts() {
            if (!this.model.bank_id) return [];
            const bank = this.banks.find(b => b.bank_id === this.model.bank_id);
            return bank ? (bank.accounts || []) : [];
        }
    },
    methods: {
        onBankChange() {
            this.selectedAccountId = null;
            this.model.bank_account_no = "";
            this.model.bank_account_name = "";
        },
        onAccountChange(accountId) {
            const acc = this.filteredAccounts.find(a => a.account_id === accountId);
            if (acc) {
                this.model.bank_account_no = acc.account_no;
                this.model.bank_account_name = acc.account_name;
            }
        }
    }
};
</script>
